import { useGanttApi } from '~/composables/api/useGanttApi'
import { useRolePermissions, actions, subjects } from '~/composables/useRolePermissions'
import { useSocket } from '~/composables/useSocket.js'

// Alerta de "presupuesto fuera del Gantt" (INV-G-25): alimenta la insignia roja
// de la entrada Planificación del menú y se refresca en vivo por socket.
// Estado y suscripción a nivel módulo (el socket ya es singleton global): solo
// hay una alerta, la de la promoción que se está viendo.
const alert = ref({
  loaded: false,
  hasAlert: false,
  unplannedChapters: 0,
  unplannedSubchapters: 0
})

const currentPromotionId = ref(null)
let socketBound = false

export const usePlanningAlert = () => {
  const { getPlanningStatus } = useGanttApi()
  const { can } = useRolePermissions()
  const { joinChannel, leaveChannel, onReload } = useSocket()

  const refresh = async () => {
    const promotionId = currentPromotionId.value
    if (!promotionId) return

    try {
      if (!can(actions.VIEW, subjects.PROMOTION_CONFIG)) return
      const response = await getPlanningStatus(promotionId)
      const status = response?.data ?? {}
      const unplanned = (status.unplannedChapters ?? 0) + (status.unplannedSubchapters ?? 0)
      alert.value = {
        loaded: true,
        hasAlert: status.hasGantt === true && unplanned > 0,
        unplannedChapters: status.unplannedChapters ?? 0,
        unplannedSubchapters: status.unplannedSubchapters ?? 0
      }
    } catch {
      // La insignia es informativa: sin lectura (403 de un rol raro, red…) no molesta
      alert.value = { loaded: true, hasAlert: false, unplannedChapters: 0, unplannedSubchapters: 0 }
    }
  }

  // Socket: altas/bajas de presupuesto y cambios de Gantt de otros usuarios.
  // Las propias recargas del usuario las ignora useSocket (triggeredBy), por lo
  // que quien muta (p. ej. planning.vue tras aplicar) llama a refresh().
  const bindSocket = () => {
    if (socketBound) return
    socketBound = true
    const reloadIfCurrent = (scope) => {
      if (scope?.promotionId && scope.promotionId === currentPromotionId.value) refresh()
    }
    onReload('budget', reloadIfCurrent)
    onReload('gantt', reloadIfCurrent)
  }

  /** La llama la navegación al entrar/salir de una promoción o cambiar de una a otra */
  const trackPromotion = (promotionId) => {
    if (promotionId === currentPromotionId.value) return
    const previous = currentPromotionId.value
    currentPromotionId.value = promotionId
    // Al cambiar de promoción se abandona el canal de la anterior: sin esto los
    // canales visitados se acumulaban toda la sesión (fix F17)
    if (previous) leaveChannel('promotion', previous)
    if (!promotionId) {
      alert.value = { loaded: false, hasAlert: false, unplannedChapters: 0, unplannedSubchapters: 0 }
      return
    }
    bindSocket()
    joinChannel('promotion', promotionId)
    refresh()
  }

  return { alert, trackPromotion, refresh }
}
